import type {
  StaffAttentionItem,
  StaffAttentionSource,
  StaffAttentionSourceKey,
  StaffAttentionSummary,
} from "@lafayette/schemas";

// "Needs you now" (CHT-2020): single customers, built from the attention items the API already sends (`GET /staff/attention`). A customer
// can have several reasons (a handoff and a request); the row shows the most urgent one and says how many more there are. The model only
// groups what the API decided: it adds no rule and no order of its own. The API lists the items of one source in its own urgency order (the
// inbox query ranks by tier, then by time), and it lists the sources urgent first. Age is never used to compare: a long wait in a low tier
// must not move before a short wait in a high tier. An item is listed only from a source that was read (`status: "ok"`) and needs action
// (not an information source).

export type NeedsTab = "all" | "conversations" | "requests";

export type NeedsReason = Readonly<{
  key: StaffAttentionSourceKey;
  severity: "urgent" | "attention";
  item: StaffAttentionItem;
}>;

export type NeedsRow = Readonly<{
  customer: StaffAttentionItem["customer"];
  /** The reason the row shows and links to. */
  reason: NeedsReason;
  /** How many other reasons this customer has in this tab. */
  more: number;
}>;

const severityRank = { urgent: 0, attention: 1 } as const;
const tabKind = { conversations: "conversation", requests: "request" } as const;

/**
 * Every item that needs action, most urgent first: urgent sources before attention sources, then the order of the API (sources in the order
 * it lists them, items in the order of their source). The sort is stable, so nothing else moves. The same item in two sources counts once
 * (the first, more urgent, wins).
 */
export function needsReasons(summary: StaffAttentionSummary): NeedsReason[] {
  const seen = new Set<string>();
  const reasons: NeedsReason[] = [];
  for (const source of summary.sources) {
    if (source.status !== "ok" || source.severity === "info") continue;
    for (const item of source.items) {
      reasons.push({ key: source.key, severity: source.severity, item });
    }
  }
  reasons.sort((left, right) => severityRank[left.severity] - severityRank[right.severity]);
  return reasons.filter((reason) => {
    const id = `${reason.item.kind}:${reason.item.id}`;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

const tabArea = { conversations: "conversations", requests: "requests" } as const;

/** The sources a tab draws from: the sources of its area, or all of them for All. */
export function sourcesForTab(
  sources: readonly StaffAttentionSource[],
  tab: NeedsTab,
): StaffAttentionSource[] {
  return sources.filter((source) => tab === "all" || source.area === tabArea[tab]);
}

/** The sources of a tab that need action and could not be read: the tab must not say "none" while one of these is blind. */
export function unreadableForTab(
  summary: StaffAttentionSummary,
  tab: NeedsTab,
): StaffAttentionSource[] {
  return sourcesForTab(
    summary.sources.filter(
      (source) => source.status === "unavailable" && source.severity !== "info",
    ),
    tab,
  );
}

/**
 * `true` when the customers named are not all the customers that wait: a source could not be read, or counted more than it names (the API
 * names at most a few items of each source, and a payment source names none). The count of the heading is then a lower bound.
 */
export function isPartial(summary: StaffAttentionSummary): boolean {
  return summary.sources.some(
    (source) =>
      source.severity !== "info" &&
      (source.status === "unavailable" || source.capped || source.count > source.items.length),
  );
}

/** The rows of one tab: one row for each customer, in the order of the customer's most urgent reason of that tab. */
export function needsRows(reasons: readonly NeedsReason[], tab: NeedsTab): NeedsRow[] {
  const shown =
    tab === "all" ? reasons : reasons.filter((reason) => reason.item.kind === tabKind[tab]);
  const byCustomer = new Map<string, NeedsReason[]>();
  for (const reason of shown) {
    const list = byCustomer.get(reason.item.customer.id) ?? [];
    list.push(reason);
    byCustomer.set(reason.item.customer.id, list);
  }
  return Array.from(byCustomer.values(), ([reason, ...rest]) => ({
    customer: (reason as NeedsReason).item.customer,
    reason: reason as NeedsReason,
    more: rest.length,
  }));
}

/**
 * How long ago, in a short word: `4m`, `3h`, `2d`. `now` is the time of the answer (`generatedAt`), not the clock of the browser, so the
 * text does not depend on the machine. A time in the future (clock skew) reads as `<1m`; no time gives `null`.
 */
export function ageText(since: string | null, now: string): string | null {
  if (since === null) return null;
  const minutes = ageMinutes(since, now);
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours}h` : `${Math.floor(hours / 24)}d`;
}

/** Whole minutes from `since` to `now`; never below 0. */
function ageMinutes(since: string, now: string): number {
  return Math.max(0, Math.floor((Date.parse(now) - Date.parse(since)) / 60_000));
}

const WAITING_KEYS: readonly StaffAttentionSourceKey[] = [
  "handoffs_at_risk",
  "coverage_exceptions",
  "handoffs_waiting",
];

export type WaitingModel =
  | Readonly<{ state: "hidden" }>
  | Readonly<{ state: "blind" }>
  | Readonly<{
      state: "ready";
      count: number;
      capped: boolean;
      /** Minutes of the longest wait. `null` when some waiting customers are not listed, so the longest wait may be one that is not known. */
      oldestMinutes: number | null;
      /** Up to four customers who wait, most urgent first. */
      customers: ReadonlyArray<StaffAttentionItem["customer"]>;
      /** The most urgent conversation: where "Start with" goes. */
      start: NeedsReason | null;
    }>;

const HERO_AVATARS = 4;

/** What the dark card says: how many wait on a person, who, and where to start. */
export function waitingModel(summary: StaffAttentionSummary): WaitingModel {
  const source = summary.sources.find((entry) => entry.key === "handoffs_waiting");
  if (source === undefined) return { state: "hidden" };
  if (source.status === "unavailable") return { state: "blind" };
  const waiting = needsReasons(summary).filter(
    (reason) => reason.item.kind === "conversation" && WAITING_KEYS.includes(reason.key),
  );
  const complete = !source.capped && source.items.length === source.count;
  const ages = source.items
    .map((item) => (item.since === null ? null : ageMinutes(item.since, summary.generatedAt)))
    .filter((value): value is number => value !== null);
  return {
    state: "ready",
    count: source.count,
    capped: source.capped,
    oldestMinutes: complete && ages.length > 0 ? Math.max(...ages) : null,
    customers: needsRows(waiting, "all")
      .slice(0, HERO_AVATARS)
      .map((row) => row.customer),
    start: waiting[0] ?? null,
  };
}
